import { useMemo, useCallback } from "react"
import { useGet } from "../utils/apiComponent/useApi"
import { endpoints } from "../utils/endpoints"
import { modulesApiResponseSchema } from "../utils/schemas/modules-schema"

/**
 * Hook to fetch modules for a specific course
 * @param {string} courseId - The ID of the course to fetch modules for
 * @returns {Object} - { modules, loading, error, refetch }
 */
export function useModules(courseId) {
  // Memoize the endpoint to prevent unnecessary re-renders
  const endpoint = useMemo(() => {
    return courseId ? `${endpoints.modules}?courseId=${courseId}` : null
  }, [courseId])
  
  const shouldFetch = Boolean(courseId)
  
  const {
    data: modulesData,
    loading,
    error,
    execute: executeFetch
  } = useGet(endpoint || `${endpoints.modules}?courseId=placeholder`, {
    schema: modulesApiResponseSchema
  })

  const refetch = useCallback(() => executeFetch(undefined, { force: true }), [executeFetch])

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(() => ({
    modules: shouldFetch ? (modulesData?.modules || []) : [],
    loading: shouldFetch ? loading : false,
    error: shouldFetch ? error : null,
    refetch: shouldFetch ? refetch : () => {},
    total: shouldFetch ? (modulesData?.total || 0) : 0,
    page: shouldFetch ? (modulesData?.page || 1) : 1,
    totalPages: shouldFetch ? (modulesData?.totalPages || 1) : 1
  }), [shouldFetch, modulesData, loading, error, refetch])
}

/**
 * Hook to fetch all modules (without course filter)
 * @returns {Object} - { modules, loading, error, refetch }
 */
export function useAllModules() {
  const {
    data: modulesData,
    loading,
    error,
    execute: refetch
  } = useGet(endpoints.modules, {
    schema: modulesApiResponseSchema
  })

  return {
    modules: modulesData?.modules || [],
    loading,
    error,
    refetch,
    total: modulesData?.total || 0,
    page: modulesData?.page || 1,
    totalPages: modulesData?.totalPages || 1
  }
}
